import {useMemo} from "react";
import type {Supplier} from "../../types/Supplier";
import {getLatLng} from "../../util/SupplierUtils";
import {MARKET_RADIUS_MILES, milesBetween} from "../../seo/markets.mjs";

interface IUseSearchRadiusFilterOptions {
  /** The yards on the map. */
  suppliers: Supplier[];
  bounds: google.maps.LatLngBounds | undefined;
  /** The searched place: the map's commanded center. */
  center: google.maps.LatLngLiteral;
  /** The user dragged the map since this search. */
  dragged: boolean;
  /** The map is still at the zoom this search opened at. */
  atSearchZoom: boolean;
}

/**
 * A search lists its market: the yards within 50 miles of the place (securspace-ui#3088).
 * The map opens with its nearer edge at that radius, but its longer side (a phone map is
 * twice as tall as it is wide) and its corners reach further. While the map is on that
 * first view, the list leaves out the yards past the radius, unless that would leave none;
 * once the user drags, zooms or pans the place out of view, the list is the map's again.
 * A pin's preview card pans the map but keeps the place in view. The previous search's
 * bounds, still reported while the map moves to this one, don't hold the place.
 */
const useSearchRadiusFilter = ({suppliers, bounds, center, dragged, atSearchZoom}: IUseSearchRadiusFilterOptions) => {
  const onSearchView = !dragged && atSearchZoom && !!bounds && bounds.contains(center);
  return useMemo(() => {
    if (!onSearchView) return suppliers;
    const nearby = suppliers.filter((supplier) => {
      const at = getLatLng(supplier);
      return !!at && milesBetween(center, at) <= MARKET_RADIUS_MILES;
    });
    return nearby.length ? nearby : suppliers;
  }, [suppliers, center, onSearchView]);
};

export default useSearchRadiusFilter;
